import { useCallback } from 'react';

function getValueByPath(obj: unknown, path: string): unknown[] {
  const segments = path.replace(/\[\]/g, '.[]').split('.');

  const traverse = (value: unknown, index: number): unknown[] => {
    if (value == null) return [];
    if (index === segments.length) return [value];

    const segment = segments[index];

    if (segment === '[]') {
      if (!Array.isArray(value)) return [];
      return value.flatMap((v) => traverse(v, index + 1));
    }

    if (typeof value === 'object') {
      return traverse((value as Record<string, unknown>)[segment], index + 1);
    }

    return [];
  };

  return traverse(obj, 0);
}

interface UseDebouncedPathSearchProps<T> {
  searchPaths?: string[];
}

export function useDebouncedOfflineSearch<T>({
  searchPaths = [],
}: UseDebouncedPathSearchProps<T>) {
  const getSearchResults = useCallback(
    (data: T[], searchKey: string): T[] => {
      if (!searchKey || searchKey.trim().length === 0) return data;

      const query = searchKey.toLowerCase();

      // CASE 1: string[]
      if (searchPaths.length === 0) {
        return (data as unknown[]).filter(
          (item) =>
            typeof item === 'string' && item.toLowerCase().includes(query)
        ) as T[];
      }

      // CASE 2: object[] with paths
      return data.filter((item) =>
        searchPaths.some((path) =>
          getValueByPath(item, path).some(
            (value) =>
              typeof value === 'string' && value.toLowerCase().includes(query)
          )
        )
      );
    },
    [searchPaths]
  );

  return { getSearchResults };
}
